import cx from 'classnames';
import React, { useCallback, useEffect, useRef } from 'react';
import { Icon, IconID, IconSize } from '../../icon';
import baseTabStyles from '../base-tab.module.scss';
const SCROLL_AMOUNT = 200;
var ScrollDirection;
(function (ScrollDirection) {
    ScrollDirection["LEFT"] = "left";
    ScrollDirection["RIGHT"] = "right";
})(ScrollDirection || (ScrollDirection = {}));
const ScrollButton = ({ direction, canScroll, onScroll, onKeyDown, }) => {
    const isLeft = direction === ScrollDirection.LEFT;
    return (React.createElement("div", { role: "button", "aria-label": `Scroll tabs ${direction}`, "aria-disabled": !canScroll, tabIndex: 0, onKeyDown: (e) => onKeyDown(e, direction), className: cx(baseTabStyles.tabNav, {
            [baseTabStyles.disabled]: !canScroll,
        }), onClick: () => onScroll(direction) },
        React.createElement(Icon, { id: isLeft ? IconID.CHEVRON_LEFT : IconID.CHEVRON_RIGHT, size: IconSize.SMALL })));
};
// Memoize ScrollButton
const MemoizedScrollButton = React.memo(ScrollButton);
const TabNavigation = ({ children, headerClass, ariaLabel, }) => {
    const [scrollState, setScrollState] = React.useState({
        canScrollLeft: false,
        canScrollRight: false,
    });
    const tabListRef = useRef(null);
    // Memoized scroll check handler
    const checkScroll = useCallback(() => {
        const el = tabListRef.current;
        if (!el)
            return;
        const scrollLeft = Math.round(el.scrollLeft);
        const maxScroll = Math.round(el.scrollWidth - el.clientWidth);
        setScrollState({
            canScrollLeft: scrollLeft > 0,
            canScrollRight: scrollLeft < maxScroll,
        });
    }, []);
    // Memoized scroll handler
    const scroll = useCallback((direction) => {
        const el = tabListRef.current;
        if (!el)
            return;
        el.scrollBy({
            left: direction === ScrollDirection.LEFT
                ? -SCROLL_AMOUNT
                : SCROLL_AMOUNT,
            behavior: 'smooth',
        });
    }, []);
    // Memoized keyboard handler
    const handleKeyDown = useCallback((e, direction) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            scroll(direction);
        }
    }, [scroll]);
    useEffect(() => {
        const tabList = tabListRef.current;
        if (tabList) {
            // Initial check
            checkScroll();
            // Scroll event listener
            tabList.addEventListener('scroll', checkScroll, {
                passive: true,
            });
        }
        return () => {
            if (tabList) {
                tabList.removeEventListener('scroll', checkScroll);
            }
        };
    }, [checkScroll]);
    return (React.createElement("div", { className: baseTabStyles.tabsWrapper },
        React.createElement(MemoizedScrollButton, { direction: ScrollDirection.LEFT, canScroll: scrollState.canScrollLeft, onScroll: scroll, onKeyDown: handleKeyDown }),
        React.createElement("ul", { className: headerClass, role: "tablist", "aria-label": ariaLabel, ref: tabListRef }, children),
        React.createElement(MemoizedScrollButton, { direction: ScrollDirection.RIGHT, canScroll: scrollState.canScrollRight, onScroll: scroll, onKeyDown: handleKeyDown })));
};
export default React.memo(TabNavigation);
//# sourceMappingURL=tab-navigation.js.map